import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { activity } from '@dha-nexus/shared';
import { activityReportCompletenessSummary } from '../../../../../packages/shared/src/activity.js';
import { PageHeader, ErrorState, LoadingSkeleton } from '../../components/index.js';
import { useApi } from '../../lib/useApi.js';
import { usePageMetadata } from '../../app/PageMetaContext.jsx';
import './admin.css';

// Friendly labels so the applied-filter chips read as plain language instead of
// raw payload keys (per the PRIME plain-language copy rule).
const FILTER_LABELS = Object.freeze({
  personId: 'Person',
  actorUserId: 'Person',
  sessionId: 'Session',
  environment: 'Environment',
  routePath: 'Route path',
  route: 'Route path',
  eventType: 'Event type',
  eventName: 'Event name',
  from: 'From',
  to: 'To',
  timeZone: 'Timezone',
  title: 'Report title',
});

function filterLabel(key) {
  return FILTER_LABELS[key] || key;
}

function filterValue(key, value, peopleById, reportTimeZone) {
  if (key === 'personId' || key === 'actorUserId') {
    const person = peopleById.get(String(value));
    return person ? `${person.displayName} · ${person.sandboxLabel}` : 'Eligible sandbox tester';
  }
  if (key === 'eventType') return activity.activityEventTypeLabel(value);
  if (key === 'eventName') return activity.activityEventNameLabel(value);
  if ((key === 'from' || key === 'to') && value) {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleString('en-US', {
        ...(reportTimeZone ? { timeZone: reportTimeZone } : {}),
        timeZoneName: 'short',
      });
    }
  }
  return String(value);
}

function statusTone(status) {
  switch (`${status || ''}`.toLowerCase()) {
    case 'completed':
      return 'sage';
    case 'running':
      return 'blue';
    case 'queued':
      return 'amber';
    case 'error':
      return 'brick';
    default:
      return 'outline';
  }
}

function formatDateTime(value) {
  if (!value) return 'Not available';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 'Not available' : parsed.toLocaleString();
}

export function ActivityReportViewerPage() {
  const { id } = useParams();
  const report = useApi(id ? `/activity-reports/${id}` : null, { skip: !id, deps: [id] });
  const run = report.data?.data || null;
  const pageTitle = run?.title || 'Activity Report Viewer';

  usePageMetadata({
    title: pageTitle,
    subtitle: 'Generated investigator artifact embedded inside DHA PRIME for review and download.',
  });

  const filterSummary = useMemo(() => {
    if (!run?.filters) return [];
    return Object.entries(run.filters).filter(([, value]) => value);
  }, [run?.filters]);
  const peopleById = useMemo(
    () =>
      new Map(
        (run?.payload?.scope?.eligiblePeople || []).map((person) => [person.personId, person]),
      ),
    [run?.payload?.scope?.eligiblePeople],
  );

  if (report.loading && !run) {
    return (
      <>
        <PageHeader title="Activity Report Viewer" subtitle="Loading report metadata." />
        <LoadingSkeleton rows={6} />
      </>
    );
  }

  if (report.error?.code === 'REPORT_SCOPE_UNAVAILABLE' && !run) {
    return (
      <>
        <PageHeader
          eyebrow="Administration · Activity investigation"
          title="Activity report unavailable"
          subtitle="This legacy activity report cannot be opened under the current sandbox tester evidence policy."
        />
        <section className="bento admin-bento">
          <div className="span-12 card empty-state" role="alert">
            <strong>Legacy activity report unavailable</strong>
            <p>{report.error.message}</p>
            <p>
              Return to Activity Reports and regenerate the evidence using the active sandbox tester
              roster.
            </p>
          </div>
        </section>
      </>
    );
  }

  if (report.error && !run) {
    return <ErrorState error={report.error} />;
  }

  if (!run) {
    return <ErrorState error={new Error('Activity report not found')} />;
  }

  const isComplete = run.status === 'completed' && run.outputUrl;
  const isPending = run.status === 'queued' || run.status === 'running';
  const completenessSummary = activityReportCompletenessSummary(run.payload?.completeness);
  const reportTimeZone = run.filters?.timeZone;

  return (
    <>
      <PageHeader
        eyebrow="Administration · Activity investigation"
        title={run.title || run.id}
        subtitle="Review the generated activity artifact in-context, or open the standalone HTML file directly."
        actions={
          <div className="row gap-8">
            {run.outputUrl ? (
              <a className="btn secondary" href={run.outputUrl} target="_blank" rel="noreferrer">
                Open artifact
              </a>
            ) : null}
          </div>
        }
      />

      <section className="bento admin-bento">
        <div className="span-12 card">
          <div className="row items-center gap-16 wrap">
            <span className={`chip ${statusTone(run.status)} sm`}>
              {activity.activityReportStatusLabel(run.status)}
            </span>
            <span className="muted small">Created {formatDateTime(run.createdAt)}</span>
            <span className="muted small">
              Requested by {run.requestedByDisplayName || run.requestedByUserId || 'Unknown'}
            </span>
          </div>
          {filterSummary.length ? (
            <div className="activity-filter-chips">
              <span className="form-label">Applied filters</span>
              <div className="row gap-8 wrap">
                {filterSummary.map(([key, value]) => (
                  <span key={key} className="chip outline sm">
                    {filterLabel(key)}: {filterValue(key, value, peopleById, reportTimeZone)}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
          {completenessSummary ? (
            <div className="activity-filter-chips" role="status">
              <span className="form-label">Report completeness</span>
              <p className="muted small">{completenessSummary}</p>
            </div>
          ) : null}
        </div>

        <div className="span-12 card">
          {isPending ? (
            <div className="empty-state">This activity report is still being generated.</div>
          ) : null}
          {run.status === 'error' ? (
            <div className="empty-state" role="alert">
              {run.payload?.error?.message || 'Report generation failed.'}
            </div>
          ) : null}
          {isComplete ? (
            <iframe
              title="Activity report artifact"
              src={run.outputUrl}
              sandbox="allow-same-origin allow-scripts allow-downloads"
              style={{
                width: '100%',
                minHeight: '75vh',
                border: '1px solid var(--color-outline-variant, #d0d7de)',
                borderRadius: '16px',
                background: '#fff',
              }}
            />
          ) : null}
        </div>
      </section>
    </>
  );
}
